import { useState } from 'react';
import { FiInfo, FiChevronDown, FiChevronUp } from 'react-icons/fi';

interface MetricsTableProps {
  metrics: {
    basic: {
      total_return: number;
      cagr: number;
      sharpe_ratio: number;
      sortino_ratio: number;
      calmar_ratio: number;
      max_drawdown: number;
      win_rate: number;
      profit_factor: number;
    };
    advanced: {
      deflated_sharpe_ratio: number;
      information_ratio: number;
      omega_ratio: number;
      tail_ratio: number;
      treynor_ratio: number;
      beta: number;
      alpha: number;
    };
    tail_risk: {
      skewness: number;
      kurtosis: number;
      var_95: number;
      cvar_95: number;
    };
    metadata: {
      start_date: string;
      end_date: string;
      trading_days: number;
      trial_count: number;
    };
  };
  showAdvanced?: boolean;
}

const metricDefinitions: Record<string, { label: string; description: string; format: (v: number) => string; isGood?: (v: number) => boolean }> = {
  // Basic
  total_return: {
    label: 'Total Return',
    description: 'Cumulative return over the entire period',
    format: (v) => `${(v * 100).toFixed(2)}%`,
    isGood: (v) => v > 0
  },
  cagr: {
    label: 'CAGR',
    description: 'Compound Annual Growth Rate',
    format: (v) => `${(v * 100).toFixed(2)}%`,
    isGood: (v) => v > 0.10
  },
  sharpe_ratio: {
    label: 'Sharpe Ratio',
    description: 'Risk-adjusted return (higher is better, >1 is good)',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 1
  },
  sortino_ratio: {
    label: 'Sortino Ratio',
    description: 'Like Sharpe but only counts downside volatility',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 1.5
  },
  calmar_ratio: {
    label: 'Calmar Ratio',
    description: 'CAGR divided by max drawdown',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 1
  },
  max_drawdown: {
    label: 'Max Drawdown',
    description: 'Largest peak-to-trough decline',
    format: (v) => `${(v * 100).toFixed(2)}%`,
    isGood: (v) => v > -0.20
  },
  win_rate: {
    label: 'Win Rate',
    description: 'Percentage of winning trades',
    format: (v) => `${(v * 100).toFixed(1)}%`,
    isGood: (v) => v > 0.50
  },
  profit_factor: {
    label: 'Profit Factor',
    description: 'Gross profit / gross loss',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 1.5
  },
  // Advanced
  deflated_sharpe_ratio: {
    label: 'Deflated Sharpe',
    description: 'Sharpe adjusted for multiple testing (data snooping)',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 0.5
  },
  information_ratio: {
    label: 'Information Ratio',
    description: 'Excess return vs benchmark per unit tracking error',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 0.5
  },
  omega_ratio: {
    label: 'Omega Ratio',
    description: 'Probability-weighted gains over losses',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 1
  },
  tail_ratio: {
    label: 'Tail Ratio',
    description: '95th percentile / 5th percentile returns',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 1
  },
  treynor_ratio: {
    label: 'Treynor Ratio',
    description: 'Excess return per unit of systematic risk (beta)',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 0
  },
  beta: {
    label: 'Beta',
    description: 'Sensitivity to market movements',
    format: (v) => v.toFixed(2),
    isGood: () => true // Neutral
  },
  alpha: {
    label: 'Alpha',
    description: 'Excess return above CAPM expected return',
    format: (v) => `${(v * 100).toFixed(2)}%`,
    isGood: (v) => v > 0
  },
  // Tail Risk
  skewness: {
    label: 'Skewness',
    description: 'Asymmetry of returns (positive = right tail, negative = left tail)',
    format: (v) => v.toFixed(2),
    isGood: (v) => v > 0
  },
  kurtosis: {
    label: 'Kurtosis',
    description: 'Tail heaviness (>3 = fat tails)',
    format: (v) => v.toFixed(2),
    isGood: (v) => v < 5
  },
  var_95: {
    label: 'VaR (95%)',
    description: 'Maximum expected daily loss at 95% confidence',
    format: (v) => `${(v * 100).toFixed(2)}%`,
    isGood: (v) => v > -0.03
  },
  cvar_95: {
    label: 'CVaR (95%)',
    description: 'Expected loss when losses exceed VaR (Expected Shortfall)',
    format: (v) => `${(v * 100).toFixed(2)}%`,
    isGood: (v) => v > -0.05
  },
};

export default function MetricsTable({ metrics, showAdvanced = false }: MetricsTableProps) {
  const [showAll, setShowAll] = useState(showAdvanced);
  const [hoveredMetric, setHoveredMetric] = useState<string | null>(null);

  const renderMetricRow = (key: string, value: number) => {
    const def = metricDefinitions[key];
    if (!def) return null;

    const isGood = def.isGood?.(value);
    const formattedValue = def.format(value);

    return (
      <tr
        key={key}
        className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
        onMouseEnter={() => setHoveredMetric(key)}
        onMouseLeave={() => setHoveredMetric(null)}
      >
        <td className="py-2 px-3">
          <div className="flex items-center gap-2">
            <span className="text-gray-300">{def.label}</span>
            {hoveredMetric === key && (
              <div className="relative group">
                <FiInfo className="w-3 h-3 text-gray-500" />
                <div className="absolute left-4 bottom-0 w-48 p-2 bg-gray-800 border border-gray-700 rounded text-xs text-gray-400 z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {def.description}
                </div>
              </div>
            )}
          </div>
        </td>
        <td className={`py-2 px-3 text-right tabular-nums font-medium ${
          isGood === true ? 'text-profit' :
          isGood === false ? 'text-loss' :
          'text-white'
        }`}>
          {formattedValue}
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
        <h3 className="font-medium text-white">Performance Metrics</h3>
        <button
          onClick={() => setShowAll(!showAll)}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
        >
          {showAll ? 'Basic' : 'Advanced'}
          {showAll ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {/* Basic Metrics */}
            <tr className="bg-gray-800/30">
              <td colSpan={2} className="py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Core Metrics
              </td>
            </tr>
            {Object.entries(metrics.basic).map(([key, value]) => renderMetricRow(key, value))}

            {/* Advanced Metrics */}
            {showAll && (
              <>
                <tr className="bg-gray-800/30">
                  <td colSpan={2} className="py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Advanced Metrics
                  </td>
                </tr>
                {Object.entries(metrics.advanced).map(([key, value]) => renderMetricRow(key, value))}

                <tr className="bg-gray-800/30">
                  <td colSpan={2} className="py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tail Risk
                  </td>
                </tr>
                {Object.entries(metrics.tail_risk).map(([key, value]) => renderMetricRow(key, value))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Metadata footer */}
      <div className="px-4 py-2 border-t border-gray-800 text-xs text-gray-500 flex items-center justify-between">
        <span>{metrics.metadata.start_date} to {metrics.metadata.end_date}</span>
        <span>{metrics.metadata.trading_days} trading days</span>
      </div>
    </div>
  );
}
